<p-table
    [value]="results"
    [loading]="loading"
    [scrollable]="true"
    scrollHeight="flex"
    [paginator]="results.length > 15"
    [rows]="15"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="{first}–{last} of {totalRecords}"
    dataKey="ein"
    [expandedRowKeys]="expandedRows"
    styleClass="p-datatable-sm p-datatable-gridlines">

    <ng-template pTemplate="caption">
        <div class="flex align-items-center justify-content-between gap-2 flex-wrap">
            <span class="text-xl font-semibold">Provider Manager</span>
            <div class="flex align-items-center gap-2 flex-wrap">
                <span class="p-input-icon-left">
                    <i class="pi pi-building"></i>
                    <input pInputText type="text" [(ngModel)]="filter.name" (keydown)="onKeyDown($event)" placeholder="Practice name" />
                </span>
                <span class="p-input-icon-left">
                    <i class="pi pi-hashtag"></i>
                    <input pInputText type="text" [(ngModel)]="filter.ein" (keydown)="onKeyDown($event)" placeholder="EIN" />
                </span>
                <span class="p-input-icon-left">
                    <i class="pi pi-map-marker"></i>
                    <input pInputText type="text" [(ngModel)]="filter.location" (keydown)="onKeyDown($event)" placeholder="City, state or zip" />
                </span>
                <p-buttonGroup>
                    <p-button label="Search" icon="pi pi-search" (onClick)="search()" [loading]="loading" [disabled]="!hasFilter"></p-button>
                    <p-button *ngIf="searched" icon="pi pi-times" severity="danger" (onClick)="clear()" pTooltip="Clear"></p-button>
                </p-buttonGroup>
            </div>
        </div>
    </ng-template>

    <ng-template pTemplate="header">
        <tr>
            <th style="width: 5rem"></th>
            <th pSortableColumn="name" style="width: 35%">Name <p-sortIcon field="name"/></th>
            <th style="width: 15%">EIN</th>
        </tr>
    </ng-template>

    <ng-template pTemplate="body" let-row let-expanded="expanded">
        <tr>
            <td>
                <div class="flex align-items-center gap-1">
                    <p-button
                        [pRowToggler]="row"
                        [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                        [rounded]="true" [text]="true" size="small" />
                    <p-tag
                        *ngIf="row.facilities?.length"
                        [value]="row.facilities.length"
                        severity="secondary"
                        styleClass="text-xs" />
                </div>
            </td>
            <td>
                <div class="flex align-items-center gap-2">
                    <ng-container *ngIf="pendingNames[row.ein] === undefined; else editingPracticeNameTpl">
                        <span
                            class="font-medium"
                            [class.cursor-pointer]="row.aliases?.length"
                            [class.text-primary]="row.aliases?.length"
                            (click)="showAliases($event, row)">
                            {{displayName(row)}}
                        </span>
                        <p-button
                            icon="pi pi-pencil"
                            size="small" [text]="true" [rounded]="true" severity="info"
                            pTooltip="Edit practice name"
                            (onClick)="startEditPracticeName(row)" />
                    </ng-container>
                    <ng-template #editingPracticeNameTpl>
                        <input
                            pInputText
                            class="p-inputtext-sm"
                            style="max-width: 16rem"
                            [(ngModel)]="pendingNames[row.ein]"
                            (keydown.enter)="savePracticeName(row)" />
                        <p-button
                            icon="pi pi-check"
                            size="small" [text]="true" [rounded]="true" severity="success"
                            pTooltip="Save name"
                            [loading]="savingNames.has(row.ein)"
                            (onClick)="savePracticeName(row)" />
                        <p-button
                            icon="pi pi-times"
                            size="small" [text]="true" [rounded]="true" severity="danger"
                            pTooltip="Cancel"
                            [disabled]="savingNames.has(row.ein)"
                            (onClick)="cancelPending(row.ein)" />
                    </ng-template>
                </div>
            </td>
            <td>{{row.ein || '—'}}</td>
        </tr>
    </ng-template>

    <ng-template pTemplate="rowexpansion" let-row>
        <tr>
            <td colspan="3" class="p-0">
                <div class="px-4 py-3 surface-50">

                    <!-- Organizational Providers -->
                    <div>
                        <div class="text-sm font-semibold text-color-secondary mb-2">Organizational Providers</div>

                        <div *ngIf="!row.facilities?.length" class="text-sm text-color-secondary mb-2">
                            No organizational providers found.
                        </div>

                        <p-accordion *ngIf="row.facilities?.length" [multiple]="true">
                            <p-accordionTab
                                *ngFor="let provider of visibleFacilities(row)"
                                (selectedChange)="onOrgProviderToggle(row, provider, $event)">
                                <ng-template pTemplate="header">
                                    <div class="flex align-items-center gap-2 flex-1">
                                        <i class="pi pi-building text-primary text-sm"></i>
                                        <ng-container *ngIf="orgNameDrafts[provider.npi] === undefined; else editingOrgNameTpl">
                                            <span class="font-medium">{{provider.organizationName}}</span>
                                            <span class="text-color-secondary text-sm font-mono">NPI {{provider.npi}}</span>
                                            <span class="flex-1"></span>
                                            <p-button
                                                icon="pi pi-pencil"
                                                size="small" [text]="true" [rounded]="true" severity="info"
                                                pTooltip="Edit organization name"
                                                (click)="$event.stopPropagation(); startEditOrgName(provider)" />
                                            <p-button
                                                icon="pi pi-trash"
                                                size="small" [text]="true" [rounded]="true" severity="danger"
                                                pTooltip="Remove organizational provider"
                                                [loading]="deletingOrg.has(provider.npi)"
                                                (click)="$event.stopPropagation(); deleteOrgProvider(row, provider.npi)" />
                                        </ng-container>
                                        <ng-template #editingOrgNameTpl>
                                            <input
                                                pInputText
                                                class="p-inputtext-sm"
                                                style="max-width: 18rem"
                                                [(ngModel)]="orgNameDrafts[provider.npi]"
                                                (click)="$event.stopPropagation()"
                                                (keydown.enter)="saveOrgName(provider)" />
                                            <span class="text-color-secondary text-sm font-mono">NPI {{provider.npi}}</span>
                                            <span class="flex-1"></span>
                                            <p-button
                                                icon="pi pi-check"
                                                size="small" [text]="true" [rounded]="true" severity="success"
                                                pTooltip="Save name"
                                                [loading]="savingOrgName.has(provider.npi)"
                                                (click)="$event.stopPropagation(); saveOrgName(provider)" />
                                            <p-button
                                                icon="pi pi-times"
                                                size="small" [text]="true" [rounded]="true" severity="danger"
                                                pTooltip="Cancel"
                                                [disabled]="savingOrgName.has(provider.npi)"
                                                (click)="$event.stopPropagation(); cancelEditOrgName(provider.npi)" />
                                        </ng-template>
                                    </div>
                                </ng-template>

                                <!-- Provider Locations -->
                                <div *ngIf="isLoadingLocations(row, provider)" class="text-sm text-color-secondary">
                                    <i class="pi pi-spin pi-spinner mr-1"></i>Loading locations…
                                </div>

                                <div *ngIf="!isLoadingLocations(row, provider) && !provider.providerLocations.length" class="text-sm text-color-secondary mb-2">
                                    No provider locations found.
                                </div>

                                <p-accordion *ngIf="provider.providerLocations.length" [multiple]="true">
                                    <p-accordionTab *ngFor="let location of provider.providerLocations; let locationIndex = index">
                                        <ng-template pTemplate="header">
                                            <div class="flex align-items-center gap-2 flex-1">
                                                <i class="pi pi-map-marker text-primary text-sm"></i>
                                                <span class="font-medium">{{location.street1}}<ng-container *ngIf="location.street2">, {{location.street2}}</ng-container>, {{location.city}}, {{location.state}} {{location.zipCode}}</span>
                                                <span class="flex-1"></span>
                                                <p-tag
                                                    *ngIf="location.professionals.length"
                                                    [value]="location.professionals.length.toString()"
                                                    severity="secondary"
                                                    styleClass="text-xs" />
                                                <p-button icon="pi pi-pencil" size="small" [text]="true" [rounded]="true" severity="info"
                                                    pTooltip="Edit location"
                                                    (click)="$event.stopPropagation(); editProviderLocation(row, provider, locationIndex)" />
                                                <p-button icon="pi pi-trash" size="small" [text]="true" [rounded]="true" severity="danger"
                                                    pTooltip="Remove location"
                                                    [loading]="deletingLocation.has(location.locationId)"
                                                    (click)="$event.stopPropagation(); removeProviderLocation(row, provider, locationIndex)" />
                                            </div>
                                        </ng-template>

                                        <!-- Professional Providers -->
                                        <div class="text-sm font-semibold text-color-secondary mb-2">Professional Providers</div>

                                        <div *ngIf="!location.professionals.length" class="text-sm text-color-secondary mb-2">
                                            No professional providers found.
                                        </div>

                                        <div *ngFor="let pro of location.professionals; let proIndex = index"
                                             class="flex align-items-center gap-2 text-sm mb-1">
                                            <i class="pi pi-user text-primary text-xs"></i>
                                            <span>{{pro.firstName}} {{pro.lastName}}</span>
                                            <span class="font-mono text-xs text-color-secondary">· NPI {{pro.npi}}</span>
                                            <p-button
                                                icon="pi pi-pencil"
                                                size="small" [text]="true" [rounded]="true" severity="info"
                                                pTooltip="Edit professional provider"
                                                (onClick)="openEditProfessionalDialog(location, proIndex)" />
                                            <p-button
                                                icon="pi pi-trash"
                                                size="small" [text]="true" [rounded]="true" severity="danger"
                                                pTooltip="Remove professional provider"
                                                [loading]="deletingPro.has(pro.npi)"
                                                (onClick)="deleteProfessional(row, provider, location, pro.npi)" />
                                        </div>

                                        <div class="mt-2">
                                            <p-button
                                                label="Add Professional Provider"
                                                icon="pi pi-plus"
                                                size="small" [text]="true"
                                                (onClick)="openProDialog(row, provider, location)" />
                                        </div>
                                    </p-accordionTab>
                                </p-accordion>

                                <div class="flex gap-2 mt-2">
                                    <p-button
                                        label="Add Location"
                                        icon="pi pi-plus"
                                        size="small" [text]="true"
                                        (onClick)="addProviderLocation(row, provider)" />
                                    <p-button
                                        label="Link Existing Location"
                                        icon="pi pi-link"
                                        size="small" [text]="true"
                                        (onClick)="openLinkLocationDialog(row, provider)" />
                                </div>
                            </p-accordionTab>
                        </p-accordion>

                        <div *ngIf="row.facilities?.length > facilityPreviewLimit" class="mt-1">
                            <p-button
                                [label]="expandedFacilities.has(row.ein)
                                    ? 'Show less'
                                    : 'Show ' + (row.facilities.length - facilityPreviewLimit) + ' more'"
                                [icon]="expandedFacilities.has(row.ein) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                [text]="true"
                                size="small"
                                (onClick)="toggleFacilitiesExpansion(row.ein)" />
                        </div>

                        <div class="mt-2 flex gap-2">
                            <p-button
                                label="Add Organizational Provider"
                                icon="pi pi-plus"
                                size="small" [text]="true"
                                (onClick)="openOrgDialog(row)" />
                            <ng-container *ngIf="showPracticeRates">
                                <p-button
                                    label="Manage Markup"
                                    icon="pi pi-percentage"
                                    size="small" [text]="true"
                                    (onClick)="openMarkupDialog(row)" />
                                <p-button
                                    label="Manage Discount"
                                    icon="pi pi-percentage"
                                    size="small" [text]="true"
                                    (onClick)="openDiscountDialog(row)" />
                            </ng-container>
                        </div>

                        <lib-address-editor
                            [visible]="locationEditorVisible"
                            (visibleChange)="locationEditorVisible = $event"
                            [address]="editingAddress"
                            (save)="saveProviderLocation($event)">
                        </lib-address-editor>

                        <p-dialog header="Link Existing Location" [(visible)]="linkLocationDialogVisible" [modal]="true" appendTo="body" [style]="{ width: '30rem' }">
                            <app-facility-location-search [(facility)]="linkingFacility" placeholder="Search by facility name or address"></app-facility-location-search>
                            <ng-template pTemplate="footer">
                                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="linkLocationDialogVisible = false" />
                                <p-button label="Link" icon="pi pi-link" [disabled]="!linkingFacility" (onClick)="confirmLinkLocation()" />
                            </ng-template>
                        </p-dialog>
                    </div>

                </div>
            </td>
        </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
        <tr>
            <td colspan="3" class="text-center text-color-secondary py-4">
                <span *ngIf="!searched">Enter a name, EIN, or location above to search.</span>
                <span *ngIf="searched && !loading">No practices found matching your filters.</span>
            </td>
        </tr>
    </ng-template>

</p-table>

<p-overlayPanel #aliasOverlay>
    <div class="flex flex-column gap-1" style="min-width: 14rem">
        <div
            *ngFor="let alias of activePractice?.aliases"
            class="px-3 py-2 border-round cursor-pointer hover:surface-200"
            (click)="selectAlias(alias)">
            {{alias}}
        </div>
    </div>
</p-overlayPanel>

<p-dialog
    header="Add Organizational Provider"
    [(visible)]="showOrgDialog"
    [modal]="true"
    [style]="{ width: '24rem' }"
    (onHide)="closeOrgDialog()">
    <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Provider NPI</label>
            <input
                pInputText
                [(ngModel)]="orgNpiInput"
                placeholder="Enter NPI"
                (keydown.enter)="confirmAddOrgProvider()" />
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="closeOrgDialog()" />
        <p-button
            label="Add"
            icon="pi pi-plus"
            [loading]="addingOrg"
            [disabled]="!orgNpiInput.trim()"
            (onClick)="confirmAddOrgProvider()" />
    </ng-template>
</p-dialog>

<app-practice-markup-form
    *ngIf="showPracticeRates"
    [(visible)]="markupDialogVisible"
    [ein]="ratesEin"
    [practiceName]="ratesPracticeName">
</app-practice-markup-form>

<app-practice-discount-form
    *ngIf="showPracticeRates"
    [(visible)]="discountDialogVisible"
    [ein]="ratesEin"
    [practiceName]="ratesPracticeName">
</app-practice-discount-form>

<p-dialog
    header="Add Professional Provider"
    [(visible)]="showProDialog"
    [modal]="true"
    [style]="{ width: '24rem' }"
    (onHide)="closeProDialog()">
    <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Professional NPI</label>
            <input
                pInputText
                [(ngModel)]="proNpiInput"
                placeholder="Enter NPI"
                (keydown.enter)="confirmAddProfessional()" />
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="closeProDialog()" />
        <p-button
            label="Add"
            icon="pi pi-plus"
            [loading]="addingPro"
            [disabled]="!proNpiInput.trim()"
            (onClick)="confirmAddProfessional()" />
    </ng-template>
</p-dialog>

<p-dialog
    header="Edit Professional Provider"
    [(visible)]="proEditDialogVisible"
    [modal]="true"
    [style]="{ width: '24rem' }"
    (onHide)="closeProEditDialog()">
    <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">First Name</label>
            <input pInputText [(ngModel)]="proEditFirstName" placeholder="First name" />
        </div>
        <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Last Name</label>
            <input pInputText [(ngModel)]="proEditLastName" placeholder="Last name" (keydown.enter)="confirmEditProfessional()" />
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="closeProEditDialog()" />
        <p-button
            label="Save"
            icon="pi pi-check"
            [loading]="savingProEdit"
            [disabled]="!proEditFirstName.trim() || !proEditLastName.trim()"
            (onClick)="confirmEditProfessional()" />
    </ng-template>
</p-dialog>
